import {
  applySnapshot,
  Instance,
  SnapshotIn,
  SnapshotOut,
  getParent,
  getRoot,
  flow,
  types,
} from 'mobx-state-tree';
import { debug, request, utils } from 'lib';
import { MysticModel } from 'models/user-model';
import maxBy from 'lodash/maxBy';

const initState = {};

const InvitedRoomModel = types.model('InvitedRoomModel', {
  id: 0,
  mm_no: '',
  nick_name: '',
  pic: '',
  invite_time: 0,
});

const RankUserModel = types
  .model('RankUserModel', {
    uid: 0,
    pic: '',
    value: 0,
    nickname: '',
    mystic: types.optional(MysticModel, {}),
  })
  .views((self) => {
    return {
      get afterPic() {
        return self.mystic.open ? self.mystic.pic : self.pic;
      },
      get afterNickname() {
        return self.mystic.open ? self.mystic.nick_name : self.nickname;
      },
      get afterId() {
        return self.mystic.open ? self.mystic.id : self.uid;
      },
    };
  });

export const MultiPkInfoModel = types
  .model('MultiPkInfoModel', {
    room_id: 0,
    // 1-胜利 2-失败 3-平局 默认为0
    pk_result: 0,
    pk_value: 0,
    top_user: types.array(RankUserModel),
    mvp_user: types.optional(RankUserModel, {}),
  })
  .views((self) => {
    return {
      get isTop() {
        return getParent(self, 2).top_room === self.room_id;
      },
    };
  });

const ModeInfoModel = types
  .model('ModeInfoModel', {
    // 1-准备阶段 2-pk阶段 3-结算阶段
    stage: 0,
    stage_desc: '',
    // 当前阶段持续时间
    duration: 0,
    // 当前阶段剩余时间
    remain_time: 0,
    top_room: 0,
    multi_pk_info: types.array(MultiPkInfoModel),
  })
  .views((self) => {
    return {
      get currentRoomValue() {
        const { roomId } = getRoot(self).room;
        const pkInfo = self.multi_pk_info.find((item) => {
          return item.room_id === roomId;
        });
        return pkInfo ? pkInfo.pk_value : 0;
      },
      get otherRoomValue() {
        const { roomId } = getRoot(self).room;
        const pkInfo = self.multi_pk_info.find((item) => {
          return item.room_id !== roomId;
        });
        return pkInfo ? pkInfo.pk_value : 0;
      },
      get totalValue() {
        return self.multi_pk_info.reduce((total, item) => {
          return total + item.pk_value;
        }, 0);
      },
      get remainTimeText() {
        return utils.getTimerText(self.remain_time);
      },
      // get isDoublePk() {
      //   return self.multi_pk_info.length === 2;
      // },
      get isShowResult() {
        return self.stage === 3;
      },
      get isShowRank() {
        return self.stage === 2;
      },
    };
  })
  .actions((self) => {
    const decreaseRemainTime = () => {
      if (self.remain_time > 0) {
        self.remain_time--;
      }
    };
    const getRoomPkInfo = (roomId) => {
      return self.multi_pk_info.find((item) => {
        return item.room_id === roomId;
      });
    };
    const updateTopUser = (roomId, topUsers) => {
      const pkInfo = getRoomPkInfo(roomId);
      if (pkInfo) {
        pkInfo.top_user = topUsers;
      }
    };
    const updatePkValue = (roomId, value, top_room) => {
      const pkInfo = getRoomPkInfo(roomId);
      if (pkInfo) {
        pkInfo.pk_value = value;
      }
      self.top_room = top_room;
    };
    return {
      decreaseRemainTime,
      getRoomPkInfo,
      updateTopUser,
      updatePkValue,
    };
  });

const JoinLineInfoModel = types
  .model('JoinLineInfoModel', {
    // 是否在连麦中
    chatting: false,
    // 是否存在主动邀请其他主播
    inviting: false,
    // 是否在随机匹配中
    matching: false,
    // 是否被其他主播邀请
    invited: false,
    // 邀请当前主播的主播信息
    invited_room: types.optional(InvitedRoomModel, {}),

    // 1-自由连麦 2-多人pk
    mode_type: 1,
    mode_info: types.optional(ModeInfoModel, {}),
  })
  .views((self) => {
    return {
      // 是否需要开始发送心跳
      get isNeedHeart() {
        return self.chatting || self.inviting || self.invited;
      },
      get isPkMode() {
        return self.mode_type === 2;
      },
      get isDoublePk() {
        return this.isPkMode && self.mode_info.multi_pk_info.length === 2;
      },
      get isShowPkBar() {
        return this.isDoublePk && self.mode_info.stage === 2;
      },
      get isInJoinLine() {
        return self.chatting || self.inviting || self.invited || self.matching;
      },
    };
  });

const StarBaseModel = types.model('StarBaseModel', {
  id: 0,
  mm_no: '',
  pic: '',
  nickname: '',
});

const StarModel = StarBaseModel.named('StarModel').props({
  inviting: false,
  live: false,
});

const Model = types
  .model('JoinLineModel', {
    info: types.optional(JoinLineInfoModel, {}),
    historyList: types.array(StarModel),
    inviteList: types.array(StarBaseModel),
    searchStar: types.array(StarModel),
    // 是否显示邀请连麦(好友连麦)
    isShowInvitePopup: false,
    // 是否显示随机匹配连麦
    isShowRandomInvitePopup: false,
    // 是否显示连麦pk玩法
    isShowPkPopup: false,
    focusList: types.array(StarModel),
  })
  .actions((self) => {
    const updateInfo = (info) => {
      self.info = info;
    };
    const updateHistoryList = (list) => {
      self.historyList = list;
    };

    const updateFocusList = (list) => {
      self.focusList = list;
    };
    const updateInviteList = (list) => {
      self.inviteList = list;
    };
    const updateSearchStar = (info) => {
      self.searchStar = info;
    };

    const setInvitePopup = (status) => {
      self.isShowInvitePopup = status;
    };

    const setRandomInvitePopup = (status) => {
      self.isShowRandomInvitePopup = status;
    };

    const setPkPopup = (status) => {
      self.isShowPkPopup = status;
    };

    return {
      updateInfo,
      updateHistoryList,
      updateInviteList,
      updateSearchStar,
      setInvitePopup,
      setPkPopup,
      setRandomInvitePopup,
      updateFocusList,
    };
  });

export default {
  initState,
  Model,
};
